import { StyledDashboard } from "./Dashboard.styled";
import { DashBoardBarChart } from "../../components/dashBoardBarChart/dashBoardBarChart";
import { Box, Typography } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { LanguageContext } from "../../globals/LanguageContext";
import { useInvoice } from "../../stores/useInvoice";
import { DashBoardCard } from "../../components/dashboardCard/dashBoardCard";
import { YearFilterBar } from "../../components/yearFilterbar/YearFilterBar";
import { useProject } from "../../stores/useProject";
import { DashBoardDoughnutChart } from "../../components/dashboardDoughnutChart/dashboardDoughnutChart";
import { formatDateToYYYYMM } from "../../globals/utils";
import { HighestIncomeChart } from "../../components/highestIncomeChart/highestIncomeChart";

export const Dashboard = () => {
  const { dictionary } = useContext(LanguageContext);
  const { invoices } = useInvoice();
  const { projects } = useProject();
  const [currentYear, setCurrentYear] = useState<string>(
    new Date().getFullYear().toString(),
  );

  const getYearlyData = (year: string | number) => {
    const data = {
      omzet: new Array(12).fill(0),
      goederen: new Array(12).fill(0),
      varia: new Array(12).fill(0),
      uitbetalingen: new Array(12).fill(0),
      vat: new Array(12).fill(0),
      rekeningstandtotaal: new Array(12).fill(0),
      rekeningstandjaar: new Array(12).fill(0),
    };

    invoices?.forEach((inv) => {
      const date = new Date(inv.invoiceDate);
      const invYear = date.getFullYear();
      const month = date.getMonth();

      const matchesYear = year === "Totaal" || invYear === Number(year);

      if (matchesYear) {
        if (inv.hasBeenPayed && inv.type === "inkomst") {
          data.omzet[month] += Number(inv.priceExVat);
          data.vat[month] += Number(inv.vat);
        }

        if (inv.hasBeenPayed && inv.type === "uitgave") {
          data.goederen[month] += Number(inv.priceExVat);
          data.vat[month] -= Number(inv.vat);
        }

        if (inv.hasBeenPayed && inv.type === "varia") {
          data.varia[month] += Number(inv.priceExVat);
          data.vat[month] -= Number(inv.vat);
        }

        if (inv.hasBeenPayed && inv.type === "uitbetaling") {
          data.uitbetalingen[month] += Number(inv.priceExVat);
        }
      }
      if (inv.hasBeenPayed) {
        const total = Number(inv.priceExVat) + Number(inv.vat);

        if (inv.type === "inkomst") {
          data.rekeningstandtotaal[month] += total;
        } else {
          data.rekeningstandtotaal[month] -= total;
        }
      }
      if (inv.hasBeenPayed && matchesYear) {
        const total = Number(inv.priceExVat) + Number(inv.vat);

        if (inv.type === "inkomst") {
          data.rekeningstandjaar[month] += total;
        } else {
          data.rekeningstandjaar[month] -= total;
        }
      }
    });

    return data;
  };

  const profitPerMonth = useMemo(() => {
    const yearlyData = getYearlyData(currentYear);

    return yearlyData.omzet.map(
    (_, i) =>
      yearlyData.omzet[i] -
      yearlyData.goederen[i] -
      yearlyData.varia[i],
  );
  }, [currentYear])  

  const currentData = useMemo(
    () => getYearlyData(currentYear),
    [invoices, currentYear],
  );
  const previousData = useMemo(
    () => getYearlyData(Number(currentYear) - 1),
    [invoices, currentYear],
  );
  const ProfitBeforeTax = useMemo(() => {
    return currentData.omzet.map(
      (value, i) => value - currentData.goederen[i] - currentData.varia[i],
    );
  }, [currentData]);
  const VATbyQuarter = useMemo(() => {
    const quarters = [0, 1, 2, 3].map((q) =>
      currentData.vat
        .slice(q * 3, q * 3 + 3)
        .reduce((acc, val) => acc + val, 0),
    );
    return quarters;
  }, [currentData]);
  const Top10Income = useMemo(() => {
    if (!invoices || invoices.length === 0) return [];

    const projectTotals = new Map<number, number>();

    invoices.forEach((inv) => {
      if (!inv.project?.id) return;

      const total = Number(inv.priceExVat) + Number(inv.vat);

      if (inv.type === "inkomst") {
        projectTotals.set(
          inv.project.id,
          (projectTotals.get(inv.project.id) || 0) + total,
        );
      }

      if (inv.type === "uitgave") {
        projectTotals.set(
          inv.project.id,
          (projectTotals.get(inv.project.id) || 0) - total,
        );
      }
    });

    const entries = [...projectTotals.entries()];
    if (entries.length === 0) return [];

    const sorted = entries.sort((a, b) => b[1] - a[1]);
    const top10 = sorted.slice(0, 10);

    return top10
      .map(([projectId, totalIncome]) => {
        const project = projects?.find((p) => p.id === projectId);
        return project ? { ...project, totalIncome } : null;
      })
      .filter(Boolean) as Array<any>;
  }, [invoices, projects]);

  const totals = {
    omzet: currentData.omzet.reduce((acc, v) => acc + v, 0),
    goederen: currentData.goederen.reduce((acc, v) => acc + v, 0),
    varia: currentData.varia.reduce((acc, v) => acc + v, 0),
    uitbetalingen: currentData.uitbetalingen.reduce((acc, v) => acc + v, 0),
    winstVoorBelasting: ProfitBeforeTax.reduce((acc, v) => acc + v, 0),
    rekeningstandtotaal: currentData.rekeningstandtotaal.reduce(
      (acc, v) => acc + v,
      0,
    ),
    rekeningstandjaar: currentData.rekeningstandjaar
      .reduce((acc, v) => acc + v, 0)
      .toFixed(2),
    btw: currentData.vat.reduce((acc, v) => acc + v, 0),
  };

  const marginPercentage =
    totals.omzet && totals.omzet !== 0
      ? (totals.goederen / totals.omzet) * 100
      : 0;

  const previousTotals = {
    omzet: previousData.omzet.reduce((acc, v) => acc + v, 0),
    goederen: previousData.goederen.reduce((acc, v) => acc + v, 0),
    varia: previousData.varia.reduce((acc, v) => acc + v, 0),
    uitbetalingen: previousData.uitbetalingen.reduce((acc, v) => acc + v, 0),
    winstVoorBelasting: previousData.omzet
      .map(
        (_, i) =>
          previousData.omzet[i] -
          previousData.goederen[i] -
          previousData.varia[i] -
          previousData.uitbetalingen[i],
      )
      .reduce((acc, v) => acc + v, 0),
  };

  const calculatePercentageChange = (
    current: number,
    previous: number,
    year: string,
  ): string => {
    if (year === "Totaal") return "+100%";
    if (!previous || previous <= 0) return "∞%";
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? "+" : "-"}${change.toFixed(2)}%`;
  };

  const dashBoardCardsLeft = [
    {
      title: "Omzet",
      amount: totals.omzet,
      comparison: calculatePercentageChange(
        totals.omzet,
        previousTotals.omzet,
        currentYear,
      ),
      compareText: "% vs vorig jaar",
    },
    {
      title: "Goederen",
      amount: totals.goederen,
      comparison: calculatePercentageChange(
        totals.goederen,
        previousTotals.goederen,
        currentYear,
      ),
      compareText: "% vs vorig jaar",
    },
    {
      title: "Varia",
      amount: totals.varia,
      comparison: calculatePercentageChange(
        totals.varia,
        previousTotals.varia,
        currentYear,
      ),
      compareText: "% vs vorig jaar",
    },
    {
      title: "Uitbetalingen",
      amount: totals.uitbetalingen,
      comparison: calculatePercentageChange(
        totals.uitbetalingen,
        previousTotals.uitbetalingen,
        currentYear,
      ),
      compareText: "% vs vorig jaar",
    },
    // {
    //   title: "Winst voor belasting",
    //   amount: totals.winstVoorBelasting,
    //   comparison: calculatePercentageChange(totals.winstVoorBelasting, previousTotals.winstVoorBelasting, currentYear),
    //   compareText: "% vs vorig jaar",
    // },
    // {
    //   title: "BTW",
    //   amount: VATbyQuarter.reduce((acc, v) => acc + v, 0), // totaal BTW per jaar (number)
    //   comparison: VATDifference, // verschil per kwartaal (number[])
    //   compareText: "Bedrag vs vorig kwartaal (€)",
    // },
  ];

  const dashBoardCardsRight = [
    {
      title: "EBT",
      amount: totals.winstVoorBelasting,
      comparison: calculatePercentageChange(
        totals.winstVoorBelasting,
        previousTotals.winstVoorBelasting,
        currentYear,
      ),
      compareText: "% vs vorig jaar",
    },
    {
      title: "Rekeningstand",
      amount: totals.rekeningstandtotaal,
      comparison: `${totals.rekeningstandjaar > 0 ? "+" : ""} €${totals.rekeningstandjaar}`,
      compareText: "Saldo toename " + currentYear,
    },
    {
      title: "BTW",
      amount: VATbyQuarter.reduce((acc, v) => acc + v, 0),
      comparison: "0",
      compareText: "Bedrag vs vorig kwartaal (€)",
    },
    {
      title: "Hoogste inkomst",
      amount: Top10Income ? Number(Top10Income[0]?.totalIncome) : 0,
      comparison: "+100%",
      compareText:
        Top10Income.length > 0
          ? `PRO-${formatDateToYYYYMM(new Date(Top10Income[0].creationDate))}-${Top10Income[0].code}`
          : "",
    },
  ];

  return (
    <StyledDashboard>
      <Box className="header">
        <Typography variant="h1">{dictionary.pages.dashboard.title}</Typography>
      </Box>
      <Box className="filterbar">
        {invoices && (
          <YearFilterBar
            invoices={invoices}
            onYearChange={(year) => setCurrentYear(year)}
          />
        )}
      </Box>
      <Box className="row">
        <Box className="cards">
          {dashBoardCardsLeft.map(
            ({ title, amount, comparison, compareText }) => (
              <DashBoardCard
                title={title}
                amount={amount}
                comparison={comparison}
                compareText={compareText}
              />
            ),
          )}
        </Box>
        <Box className="chartWrapper">
          <DashBoardDoughnutChart chartData={marginPercentage} />
        </Box>
        <Box className="cards">
          {dashBoardCardsRight.map(
            ({ title, amount, comparison, compareText }) => (
              <DashBoardCard
                title={title}
                amount={amount}
                comparison={comparison}
                compareText={compareText}
              />
            ),
          )}
        </Box>
      </Box>
      <Box className="row">
        <Box className="chart1">
          <DashBoardBarChart
            incomePerMonth={getYearlyData(currentYear).omzet}
            goodsPerMonth={getYearlyData(currentYear).goederen}
            variousPerMonth={getYearlyData(currentYear).varia}
            profitPerMonth={profitPerMonth}
            currentYear={currentYear}
            payoutPerMonth={getYearlyData(currentYear).uitbetalingen}
          />
        </Box>
        <Box className="chart1">
          <HighestIncomeChart top10Income={Top10Income} />
        </Box>
      </Box>
    </StyledDashboard>
  );
};
